import type {
    SketchParametricParameterDescriptor,
    SketchParametricParameterRole,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';

/**
 * 草图参数向量。
 *
 * 设计说明：
 * 1. 以稳定 parameterId 注册草图几何变量（点局部坐标、圆半径）；
 * 2. 区分固定/自由参数，向求解器暴露自由参数视图；注册顺序稳定，
 *    是秩分析可复现与 undo/redo 后分支不漂移的前提；
 * 3. 快照/恢复支撑“候选失败恢复 before”与分支 seed 语义（02 计划 §7.5）。
 */
export class SketchParametricParameterVector {
    /** 按注册顺序保存的参数描述。 */
    private readonly descriptors: SketchParametricParameterDescriptor[] = [];

    /** 参数 ID 到描述的索引。 */
    private readonly descriptorById = new Map<string, SketchParametricParameterDescriptor>();

    /** 单次求解内部的固定状态覆盖，不修改只读领域参数描述。 */
    private readonly fixedStateOverrideById = new Map<string, boolean>();

    /**
     * 注册一个参数。
     *
     * @param parameterId 参数稳定 ID。
     * @param role 参数角色。
     * @param value 初始值（米/弧度）。
     * @param fixed 是否固定。
     * @throws 当参数 ID 已注册，或初始值不是有限数值时抛出错误。
     */
    public addParameter(
        parameterId: string,
        role: SketchParametricParameterRole,
        value: number,
        fixed: boolean,
    ): void {
        if (this.descriptorById.has(parameterId)) {
            throw new Error(`参数已注册：${parameterId}`);
        }
        if (!Number.isFinite(value)) {
            throw new Error(`参数初始值必须是有限数值：${parameterId}=${String(value)}`);
        }
        this.assertValueInDomain(parameterId, role, value);

        const descriptor: SketchParametricParameterDescriptor = {parameterId, role, value, fixed};

        this.descriptors.push(descriptor);
        this.descriptorById.set(parameterId, descriptor);
    }

    /**
     * 判断参数是否已注册。
     *
     * @param parameterId 参数 ID。
     * @returns 是否已注册。
     */
    public has(parameterId: string): boolean {
        return this.descriptorById.has(parameterId);
    }

    /**
     * 判断指定参数是否固定。
     *
     * @param parameterId 参数 ID。
     * @returns 参数不参与自由变量求解时返回 true。
     * @throws 参数不存在时抛出明确错误。
     */
    public isFixed(parameterId: string): boolean {
        const descriptor = this.descriptorById.get(parameterId);

        if (!descriptor) {
            throw new Error(`参数不存在：${parameterId}`);
        }

        return this.fixedStateOverrideById.get(parameterId) ?? descriptor.fixed;
    }

    /**
     * 临时切换参数固定状态。
     *
     * 本入口只服务一次求解内部的严格操作候选；调用方必须配合固定状态快照恢复，
     * 禁止把它当作草图 Fix 关系写入口。
     *
     * @param parameterId 参数 ID。
     * @param fixed 新固定状态。
     * @throws 参数不存在时抛出明确错误。
     */
    public setFixed(parameterId: string, fixed: boolean): void {
        const descriptor = this.descriptorById.get(parameterId);

        if (!descriptor) {
            throw new Error(`参数不存在：${parameterId}`);
        }

        if (descriptor.fixed === fixed) {
            this.fixedStateOverrideById.delete(parameterId);
            return;
        }

        this.fixedStateOverrideById.set(parameterId, fixed);
    }

    /**
     * 捕获全部参数固定状态。
     *
     * @returns 参数 ID 到固定状态的快照。
     */
    public snapshotFixedStates(): Record<string, boolean> {
        const snapshot: Record<string, boolean> = {};

        this.descriptors.forEach((descriptor) => {
            snapshot[descriptor.parameterId] = this.isFixed(descriptor.parameterId);
        });

        return snapshot;
    }

    /**
     * 恢复全部参数固定状态。
     *
     * @param snapshot 由 `snapshotFixedStates()` 创建的完整快照。
     * @throws 快照缺少任一已注册参数时抛出明确错误。
     */
    public restoreFixedStates(snapshot: Readonly<Record<string, boolean>>): void {
        // 先完整校验快照，再更新覆盖层，禁止异常时只恢复一部分参数。
        this.descriptors.forEach((descriptor) => {
            const fixed = snapshot[descriptor.parameterId];

            if (typeof fixed !== 'boolean') {
                throw new Error(`固定状态快照缺少参数：${descriptor.parameterId}`);
            }
        });

        this.fixedStateOverrideById.clear();
        this.descriptors.forEach((descriptor) => {
            const fixed = snapshot[descriptor.parameterId]!;

            if (fixed !== descriptor.fixed) {
                this.fixedStateOverrideById.set(descriptor.parameterId, fixed);
            }
        });
    }

    /**
     * 读取参数值。
     *
     * @param parameterId 参数 ID。
     * @returns 当前值。
     * @throws 当指定参数不存在时抛出错误。
     */
    public read(parameterId: string): number {
        const descriptor = this.descriptorById.get(parameterId);

        if (!descriptor) {
            throw new Error(`参数不存在：${parameterId}`);
        }

        return descriptor.value;
    }

    /**
     * 写入参数值；固定参数拒绝写入。
     *
     * @param parameterId 参数 ID。
     * @param value 新值。
     * @throws 当指定参数不存在或已被固定时抛出错误。
     */
    public write(parameterId: string, value: number): void {
        const descriptor = this.descriptorById.get(parameterId);

        if (!descriptor) {
            throw new Error(`参数不存在：${parameterId}`);
        }
        if (this.isFixed(parameterId)) {
            throw new Error(`固定参数不可写：${parameterId}`);
        }
        if (!Number.isFinite(value)) {
            throw new Error(`参数值必须是有限数值：${parameterId}=${String(value)}`);
        }
        descriptor.value = value;
    }

    /**
     * 校验当前参数向量的业务定义域。
     *
     * 说明：LM 迭代允许暂时越过正值边界，但只有最终解满足参数定义域才可写回运行时对象；
     * 这样既不在数值迭代中制造不连续截断，也不会把负半径、负槽宽或负缩放提交为稳定解。
     *
     * @throws 当参数不是有限数值，或正值参数落到退化边界时抛出错误。
     */
    public assertCurrentValuesInDomain(): void {
        this.descriptors.forEach((descriptor) => {
            if (!Number.isFinite(descriptor.value)) {
                throw new Error(
                    `参数值必须是有限数值：${descriptor.parameterId}=${String(descriptor.value)}`,
                );
            }
            this.assertValueInDomain(descriptor.parameterId, descriptor.role, descriptor.value);
        });
    }

    /**
     * 获取自由参数 ID（按注册顺序）。
     *
     * @returns 自由参数 ID 数组。
     */
    public getFreeParameterIds(): string[] {
        return this.descriptors
            .filter((descriptor) => !this.isFixed(descriptor.parameterId))
            .map((descriptor) => descriptor.parameterId);
    }

    /**
     * 获取全部参数描述（按注册顺序，只读视图）。
     *
     * @returns 参数描述数组。
     */
    public getDescriptors(): readonly SketchParametricParameterDescriptor[] {
        return this.descriptors;
    }

    /**
     * 计算当前参数岛的几何特征长度。
     *
     * 说明：优化成本需要把米制残差转换为无量纲，但不能直接除以极小收敛容差，
     * 否则会把 Jacobian 放大到破坏 LM 阻尼的数量级。这里使用点坐标跨度、半径、
     * 槽口半宽和等效弧长中的最大值，并设置统一下限，得到只在一次求解开始时冻结的尺度。
     *
     * @returns 特征长度（米）。
     */
    public calculateCharacteristicLength(): number {
        const xValues: number[] = [];
        const yValues: number[] = [];
        let scalarLength = 0;

        this.descriptors.forEach((descriptor) => {
            if (descriptor.role === 'x') {
                xValues.push(descriptor.value);
                return;
            }
            if (descriptor.role === 'y') {
                yValues.push(descriptor.value);
                return;
            }
            if (descriptor.role === 'radius'
                || descriptor.role === 'slot-half-width'
                || descriptor.role === 'scaled-angle'
                || descriptor.role === 'curve-length-parameter') {
                scalarLength = Math.max(scalarLength, Math.abs(descriptor.value));
            }
        });

        const rangeX = this.calculateFiniteRange(xValues);
        const rangeY = this.calculateFiniteRange(yValues);

        return Math.max(
            rangeX,
            rangeY,
            scalarLength,
            SKETCH_PARAMETRIC_TOLERANCE.minimumNormalizationLength,
        );
    }

    /**
     * 捕获全部参数值快照。
     *
     * @returns 参数 ID 到值的普通对象。
     */
    public snapshotValues(): Record<string, number> {
        const snapshot: Record<string, number> = {};

        this.descriptors.forEach((descriptor) => {
            snapshot[descriptor.parameterId] = descriptor.value;
        });

        return snapshot;
    }

    /**
     * 从快照恢复全部参数值。
     *
     * 说明：恢复的是历史合法值，含固定参数，绕过 write 的固定保护。
     *
     * @param snapshot 由 snapshotValues 捕获的快照。
     */
    public restoreValues(snapshot: Record<string, number>): void {
        this.descriptors.forEach((descriptor) => {
            const value = snapshot[descriptor.parameterId];

            if (value === undefined) {
                throw new Error(`快照缺少参数：${descriptor.parameterId}`);
            }
            if (!Number.isFinite(value)) {
                throw new Error(`快照参数必须是有限数值：${descriptor.parameterId}=${String(value)}`);
            }
            this.assertValueInDomain(descriptor.parameterId, descriptor.role, value);
            descriptor.value = value;
        });
    }

    /**
     * 校验单个参数的角色定义域。
     *
     * @param parameterId 参数稳定 ID。
     * @param role 参数角色。
     * @param value 待校验数值。
     * @throws 当正值参数小于或等于退化长度容差时抛出错误。
     */
    private assertValueInDomain(
        parameterId: string,
        role: SketchParametricParameterRole,
        value: number,
    ): void {
        const positiveRole = role === 'radius' || role === 'slot-half-width' || role === 'scale';

        if (positiveRole && value <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            throw new Error(
                `参数必须大于退化容差 ${SKETCH_PARAMETRIC_TOLERANCE.degenerateLength}：`
                + `${parameterId}=${String(value)}`,
            );
        }
    }

    /**
     * 计算一组有限数值的跨度。
     *
     * @param values 有限数值列表。
     * @returns 最大值减最小值；少于两个值时返回 0。
     */
    private calculateFiniteRange(values: readonly number[]): number {
        if (values.length < 2) {
            return 0;
        }

        let minimum = values[0]!;
        let maximum = values[0]!;

        for (let index = 1; index < values.length; index += 1) {
            const value = values[index]!;

            minimum = Math.min(minimum, value);
            maximum = Math.max(maximum, value);
        }

        return maximum - minimum;
    }
}
